// @ts-check
import classNames from 'classnames';
import React, { useState } from 'react';
import AccordionMenu from '~/components/Common/AccordionMenu';
import Link from '~/components/Common/Link';
import DevOnlyWrapper from '~/components/DevOnly/DevOnlyWrapper';
import { ConvertNavigationToLinkItem } from '~/components/Hocs/useGlobalLayout';

import './MobileHeader.scss';
import './MobileHeaderHamburger.scss';

/**
 * @typedef MobileHeaderRowProps
 * @property {boolean} isOpen A value indicating whether the mobile menu is open.
 * @property {import('react').MouseEventHandler<HTMLDivElement>} onToggle A callback for when header is toggled.
 * @property {import('~/components/Hocs/useGlobalLayout').HeaderOptions} options
 */
/** @type {import('~/types/React').ReactFC<MobileHeaderRowProps>} */
const MobileHeaderRow = ({ isOpen, onToggle, options, ...rest }) => (
	<div
		{...rest}
		id="navigation"
		className={classNames('mobile-header-row', isOpen ? 'opened' : 'closed')}
		role="navigation"
	>
		{/* The FDIC digital sign */}
		<div className="fdic-digital-sign-post">
			<img src="/assets/images/FDIC-header-sign.svg" alt="FDIC Digital Sign" className="digital-sign" />
		</div>

		<div className="navigation-row">
			{/* The Bank logo to the left */}
			<Link to={options?.logo?.link?.href ?? '/'}>
				<img
					src={options?.logo?.image?.url}
					alt={options?.logo?.link?.title}
					style={{ height: 36, padding: '0.25em' }}
				/>
			</Link>

			{/* The login and hamburger yum */}
			<div className="right-actions">
				<Link to={options?.login_button?.href ?? '/login'} className="mobile-login-button">
					{options?.login_button?.title}
				</Link>

				{/* The hamburger */}
				<div className={classNames('mobile-header-hamburger', { 'is-open': isOpen })} onClick={onToggle}>
					<span className="burger" />
				</div>
			</div>
		</div>
	</div>
);

/**
 * @typedef MobileHeaderProps
 * @property {string} [currentPath] The current location of the component.
 * @property {import('~/components/Hocs/useGlobalLayout').HeaderOptions} options
 */
/** @type {import('~/types/React').ReactFC<MobileHeaderProps>} */
const MobileHeader = ({ className, currentPath, options, ...rest }) => {
	const animations = [
		{ text: 'Slide Down', value: 'slide-down' },
		{ text: 'Twist In', value: 'twist-in' },
		{ text: 'Fade', value: 'fade' },
		{ text: 'None', value: '' },
	];
	const [ani, setAni] = useState(animations.length - 1);

	// Ensure the class does not exist on mount.
	React.useEffect(() => {
		setBodyScroll(false);
	}, []);

	/**
	 * @param {boolean} isMenuOpen A value indicating the new value of the menu's openness state.
	 * @returns {boolean} The same value passed in, for chaining.
	 */
	function setBodyScroll(isMenuOpen) {
		if (typeof window !== 'undefined' && window != null) {
			window.document.body.classList[!isMenuOpen ? 'remove' : 'add']('mobile-header-disable-scroll');
		}
		return isMenuOpen;
	}

	const [isOpen, setIsOpen] = useState(false);
	const [didCtrl, setDidCtrl] = useState(false);

	/** @type {import('react').MouseEventHandler<HTMLDivElement>} */
	const onToggle = (event) => {
		setDidCtrl(event.ctrlKey);
		setIsOpen((prev) => setBodyScroll(!prev));
	};

	const listItems = ConvertNavigationToLinkItem(options?.navigation?.first_level);
	const whichIndexOpen = listItems.findIndex((mi) => currentPath?.includes(mi.link));

	return (
		<div {...rest} className={classNames('mobile-header', className)}>
			<MobileHeaderRow isOpen={isOpen} onToggle={onToggle} options={options} />

			<div
				onClick={() => setIsOpen(false)}
				className={classNames('mobile-header-menu-overlay', { 'is-hidden': !isOpen })}
			/>

			<div className={classNames('mobile-header-menu', animations[ani].value, isOpen ? 'is-open' : 'is-closed')}>
				<div className="mobile-header-menu-content">
					{/* Secret animation configuration menu, only on dev, only if visible*/}
					<DevOnlyWrapper>
						{didCtrl && (
							<div className="animation-menu">
								{animations.map(({ text }, i) => (
									<button
										className={classNames('btn', { 'is-active': i === ani })}
										onClick={() => setAni(i)}
										key={`style-button-${text}`}
									>
										{text}
									</button>
								))}
							</div>
						)}
					</DevOnlyWrapper>

					{/* The locations navigation menu */}
					<AccordionMenu
						items={ConvertNavigationToLinkItem(options?.navigation?.first_level)}
						className="menu-accordion"
						initialIndexOpen={whichIndexOpen}
					/>

					{/* The extra utility links */}
					<div className="utility-container">
						{options?.util_links
							?.filter((item) => item.visible_on.includes('mobile'))
							.map((link, i) => (
								<Link
									className="utility-link"
									to={link.link.href}
									openInNewTab={link.open_in_new_tab}
									key={`util-link-${link.link.href}`}
								>
									{link.link.title}
								</Link>
							))}
					</div>

					{/* The open account online link */}
					<Link
						className="open-account-online"
						to={options?.open_account_button?.href ?? 'https://online.ozk.com/open-accounts/'}
						openInNewTab={true}
					>
						{options?.open_account_button?.title}
					</Link>

					{/* The login button */}
					<Link className="inline-login-button-container" to={options?.login_button?.href ?? '/login'}>
						<div className="inline-login-button">{options?.login_button?.title}</div>
					</Link>
				</div>
			</div>
		</div>
	);
};

export default MobileHeader;
